import { useState, useEffect } from 'react';
import { NavLink, useParams, Link } from 'react-router-dom';
import { guidesApi, Guide, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import RichTextViewer from '@/components/RichTextViewer';
import { cn } from '@/lib/utils';

export default function GuidePage() {
  const { slug } = useParams<{ slug?: string }>();
  const { user } = useAuth();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [currentGuide, setCurrentGuide] = useState<Guide | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingGuide, setIsLoadingGuide] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasBookingAccess, setHasBookingAccess] = useState(true);

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  // Load guide list
  useEffect(() => {
    const loadGuides = async () => {
      setIsLoading(true);
      try {
        const result = await guidesApi.list();
        setGuides(result.guides);
        setHasBookingAccess(result.hasBookingAccess);
      } catch (err) {
        console.error('Failed to load guides:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadGuides();
  }, []);

  // Load current guide when slug changes
  useEffect(() => {
    if (!slug) {
      setCurrentGuide(null);
      return;
    }

    const loadGuide = async () => {
      setIsLoadingGuide(true);
      setError(null);
      try {
        const result = await guidesApi.get(slug);
        setCurrentGuide(result.guide);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          if (err.code === 'BOOKING_REQUIRED') {
            setError('This guide requires an active booking to view.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Failed to load guide');
        }
        setCurrentGuide(null);
      } finally {
        setIsLoadingGuide(false);
      }
    };
    loadGuide();
  }, [slug]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Apartment Guide</h1>
        {isAdmin && (
          <Link to="/admin/guides" className="btn-secondary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Manage Guides
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="card p-4">
          <h2 className="font-medium text-gray-900 mb-4">Guide Pages</h2>
          <nav className="space-y-1">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
              </div>
            ) : guides.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">
                No guide pages yet.
              </div>
            ) : (
              guides.map((guide) => (
                <NavLink
                  key={guide.id}
                  to={`/guide/${guide.slug}`}
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2 text-sm rounded-md transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    )
                  }
                >
                  <div className="flex items-center justify-between">
                    <span>{guide.title}</span>
                    {guide.requiresBooking && (
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-label="Requires active booking"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    )}
                  </div>
                  {isAdmin && !guide.isPublished && (
                    <span className="text-xs text-amber-600">(Draft)</span>
                  )}
                </NavLink>
              ))
            )}
          </nav>

          {!hasBookingAccess && guides.some((g) => g.requiresBooking) && (
            <div className="mt-4 p-3 bg-amber-50 rounded-md">
              <p className="text-xs text-amber-700">
                Some guides require an active booking to view.
              </p>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="lg:col-span-3 card p-6">
          {isLoadingGuide ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Access Restricted
              </h3>
              <p className="mt-2 text-sm text-gray-500">{error}</p>
            </div>
          ) : currentGuide ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {currentGuide.title}
                </h2>
                {isAdmin && (
                  <Link
                    to={`/admin/guides/${currentGuide.id}/edit`}
                    className="btn-secondary text-sm"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </Link>
                )}
              </div>
              {currentGuide.content ? (
                <RichTextViewer content={currentGuide.content} />
              ) : (
                <p className="text-gray-500 italic">
                  This guide has no content yet.
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {guides.length === 0
                  ? 'No Guides Available'
                  : 'Select a Guide'}
              </h3>
              <p className="mt-2 text-sm">
                {guides.length === 0
                  ? 'Ask an administrator to create guide pages.'
                  : 'Select a guide page from the sidebar to view its content.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
